import * as WebFont from 'webfontloader';
import * as log from 'loglevel';

import Util from './util';

class FontLoader {

    // font definitions
    static getFont(name) {
        for (let font of this.fonts) {
            if (font.name === name) {
                return font;
            }
        }
        return undefined;
    }

    // custom font types: https://github.com/typekit/webfontloader#custom
    static addFvd(font, name) {
        name += ':n4'; // normal font

        // italic font
        if (font.files.italic) {
            name += ',i4';
        }
        // bold font
        if (font.files.bold) {
            name += ',n7';
        }
        // bold italic font
        if (font.files.bold_italic) {
            name += ',i7';
        }

        return name;
    }

    // syntax: https://developers.google.com/fonts/docs/getting_started#specifying_font_families_and_styles_in_a_stylesheet_url
    static addGoogleFontStyle(font, name){

        let style = [];

        if (font.files.regular) {
            if (font.files.regular_300) { // special 300 font weight
                style.push('300');
            } else {
                style.push('400');
            }
        }

        if (font.files.italic) {
            if (font.files.italic_300) { // special 300 font weight
                style.push('300italic');
            } else {
                style.push('italic');
            }
        }

        if (font.files.bold) {
            style.push('bold');
        }
        if (font.files.bold_italic) {
            style.push('bolditalic');
        }

        if(style.length){
            return name + ':' + style.join(',');
        }
    }

    static loadFontsByName(names) {
        let custom = [];
        let google = [];

        // NEW: internal queue to always wait for the last load to finish before a new load is started (to prevent parallel loading of the same fonts in the case of multi view)
        if(this._loadQueue == null){
            this._loadQueue = Promise.resolve();
        }

        this._loadQueue = this._loadQueue
            .then(() => {

                // loop over all font names and add the families
                for (let name of names) {
                    let font = this.getFont(name);

                    // font found
                    if (font) {
                        // excluded already loaded
                        if(!font.loaded){
                            switch(font.type){
                                case 0: // browser
                                    font.loaded = true;  // has to be present
                                    break;
                                case 1: // google
                                    // google font
                                    let gFontName = this.addGoogleFontStyle(font, name);
                                    if(google.indexOf(gFontName) === -1) {
                                        google.push(gFontName);
                                    }
                                    break;
                                case 2: // custom
                                    // addFvd only for custom fonts
                                    let fontName = this.addFvd(font, name);
                                    if (custom.indexOf(fontName) === -1) {
                                        custom.push(fontName);
                                    }
                                    break;
                            }
                        }
                    }
                }

                if (custom.length > 0 || google.length > 0) {
                    return this.loadFontsByFamilies(custom, google);
                } else {
                    return Promise.resolve(true);
                }

            });

        return this._loadQueue;
    }

    static loadFontsByFamilies(custom, google) {

        return new Promise((resolve, reject) => {
            
            const fontsToLoad = {
                timeout: 3000, // Set the timeout to 3 seconds
            }

            if(custom && custom.length){

                // clear font suffix
                let names = custom.map(font => font.substring(0, font.indexOf(":")));

                fontsToLoad.custom = {
                    families: custom,
                    // load dynamic custom font stylesheet via api
                    urls: [ this.fontCss(names) ]
                };
            }

            if(google && google.length){
                fontsToLoad.google = {
                    families: google,
                };
            } 

            fontsToLoad.active = () => {
                resolve();
            };

            fontsToLoad.inactive = () => {
                // always resolve to still render preview
                resolve();
            };

            fontsToLoad.fontactive =  (name, fvd) => {
                let font = FontLoader.getFont(name);

                // mark as loaded
                if(font){
                  font.loaded = true;
                } else {
                    // font not defined (google font) auto add as loaded to prevent loading again
                    FontLoader.fonts.push({
                        name: name,
                        loaded: true,
                    });
                }
            };

            fontsToLoad.fontinactive = (name, fvd) => {

                let font = FontLoader.getFont(name);

                log.warn('[live-options] failed to load font', font);

                // mark as loaded to avoid trying to load again (always causes 3 second timeout)
                font.loaded = true;
            }

            // trigger load
            WebFont.load(fontsToLoad);
        });
    }

    static fontPreview(fontFamily){
        // append to params
        let params = Util.extend(FontLoader.apiParams, { name: fontFamily });
        return FontLoader.api + '/preview?' + Util.buildQueryParams(params);
    }

    static fontCss(fontFamilies){
        // append to params
        let params = Util.extend(FontLoader.apiParams, { names: fontFamilies.join(',') });
        return FontLoader.api + '/css?' + Util.buildQueryParams(params);
    }
}


FontLoader.fonts = [];
FontLoader.api = '';
FontLoader.apiParams = {};

export default FontLoader;